import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BasicModal from "../BasicModal";
import { useTranslation } from "react-i18next";
import { useGetLatestOTWeightLossOrderQuery } from "src/api/services/ordersApi";
import { differenceInDays } from "date-fns";
import { useGetLatestPrescriptionQuery } from "src/api/services/prescriptionsApi";
import { useGenerateOTRefillLink } from "src/hooks/useGenerateOTRefillLink";
import { useNavigate } from "react-router-dom";

const OTRefillModal = () => {
  const { t } = useTranslation();
  const component = "ot-refill-modal";
  const [open, setOpen] = useState<boolean>(false);
  const { data } = useGetLatestOTWeightLossOrderQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: prescription } = useGetLatestPrescriptionQuery(
    {
      product_category: "Weight Loss",
    },
    { refetchOnMountOrArgChange: true }
  );
  const refillLink = useGenerateOTRefillLink();
  const navigate = useNavigate();
  const dismissedRef = useRef(false);

  const title = useMemo(
    () => `${t(`${component}.title`, "Time for your next refill")} 💙`,
    [t]
  );
  const text = useMemo(
    () =>
      t(
        `${component}.text`,
        "Your medication refill is now available. Ordering today helps ensure you stay on track without missing a dose."
      ),
    [t]
  );

  const disclaimer = useMemo(
    () =>
      t(
        `${component}.disclaimer`,
        "No auto-billing. You’re always in control of your refill."
      ),
    [t]
  );
  const buttonText = useMemo(() => t(`${component}.button`), [t]);

  const handleClick = useCallback(() => {
    if (refillLink == null) return null;

    return navigate({
      pathname: refillLink.path,
      search: refillLink.searchParams,
    });
  }, [navigate, refillLink]);

  const handleClose = useCallback(() => {
    dismissedRef.current = true;
    setOpen(false);
  }, []);

  useEffect(() => {
    if (dismissedRef.current) return;
    if (prescription != null) return;
    if (refillLink == null) return;
    if (data == null) return;
    if (data.delivered_at == null) return;

    const deliveryDiff = differenceInDays(
      new Date(),
      new Date(data.delivered_at)
    );

    const displayTimeout = setTimeout(() => {
      if (data.status == "delivered" && deliveryDiff >= 20) {
        setOpen(true);
        return;
      }
    }, 5000);

    return () => clearTimeout(displayTimeout);
  }, [data, prescription, refillLink]);

  return (
    <BasicModal
      buttonText={buttonText}
      text={text}
      title={title}
      disclaimer={disclaimer}
      open={open}
      onClose={handleClose}
      handleClick={handleClick}
    />
  );
};

export default OTRefillModal;
